import React, { useState, useEffect } from "react";

import {
  IonButton,
  IonContent,
  IonPage,
  IonInput,
  IonItem,
  IonLabel,
  useIonViewWillEnter,
} from "@ionic/react";

import "./ResetPassword.scss";
import { resetPasswordService } from "./ResetPassword.service";
import { ERROR } from "../../constants";
import {
  ResetLinkValidResult,
  ResetPasswordResult,
  ResetParams,
} from "./ResetPassword.model";
import Loader from "../../components/Shared/Loader/Loader";
import { AuthService } from "../../services/authService";

const ResetPasswordPage: React.FC = () => {
  const validationKeeper = {
    password: {
      req: false,
      notStrong: false,
    },
  };

  const [forgotStatus, setForgotStatus] = useState("");
  const [isFormValid, setFormStatus] = useState(false);
  const [isLinkValid, setIsLinkValid] = useState(true);
  const [resetPassword, setResetPassword] = useState("");
  const [isLoaderActive, setLoaderStatus] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isShowPassword, setPasswordStatus] = useState(false);
  const [userId, setUserId] = useState<string | undefined>("");
  const [isShowConfirmPassword, setConfirmPasswordStatus] = useState(false);
  const [validation, setValidation] = useState(validationKeeper);
  const [networkCode, setNetworkCode] = useState("");
  const [resetKeyState, setResetKeyState] = useState("");

  useIonViewWillEnter(() => {
    AuthService.clearStorage();
  });

  useEffect(() => {
    const resetKey = document.URL.split("reset-password/")[1];
    var res = resetKey.split("/");
    let reset_key = res[0];
    let network_code = res[1];
    setNetworkCode(network_code);
    setResetKeyState(reset_key);
    getUserID(reset_key, network_code);
  }, []);

  useEffect(() => {
    setFormStatus(formValidation());
  }, [resetPassword, confirmPassword]); // any state variable(s) included in here will trigger the effect to run

  useEffect(() => {
    document.body.className = "dark-theme";
  }, []);

  const formValidation = () => {
    return resetPassword && confirmPassword ? true : false;
  };

  const handleResetChange = (e: any) => {
    const { name, value } = e.target;
    setResetPassword(value);
    setForgotStatus("");
  };

  const handleConfirmChange = (e: any) => {
    const { name, value } = e.target;
    setConfirmPassword(value);
    setForgotStatus("");
  };

  const getUserID = async (resetKey: string, network_Code: string) => {
    const params = {
      reset_key: resetKey,
      reg_code: network_Code,
    };
    setLoaderStatus(true);
    const respResult: ResetLinkValidResult =
      await resetPasswordService.getUserId(params);
    if (respResult.isSuccess) {
      setIsLinkValid(true);
      setUserId(respResult!.userGuid);
    } else {
      setIsLinkValid(false);
    }
    setLoaderStatus(false);
  };

  const onPassword = () => {
    const re = new RegExp(
      "^(?=.*?[a-zA-Z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-.+/,=_]).{8,}$"
    );

    const value = "" + resetPassword;
    const isOk = re.test(value);
    if (!isOk) {
      validationKeeper.password.notStrong = true;
      setValidation(validationKeeper);
    } else {
      validationKeeper.password.notStrong = false;
      setValidation(validationKeeper);
    }
  };

  async function onSubmit(e: any) {
    e.preventDefault();
    if (
      resetPassword === confirmPassword &&
      resetPassword.length >= 8 &&
      !validation.password.notStrong
    ) {
      setLoaderStatus(true);
      let params: ResetParams = {
        guid: userId,
        password: resetPassword,
        reg_code: networkCode,
        reset_key: resetKeyState,
      };
      const respResult: ResetPasswordResult =
        await resetPasswordService.resetPassword(params);
      if (respResult.isSuccess) {
        window.location.href = "/login";
      } else {
        setForgotStatus(respResult.message);
      }

      setLoaderStatus(false);
    } else {
      resetPassword.length >= 8
        ? setForgotStatus("Passwords do not match")
        : setForgotStatus("Your password must contain 8 characters");
    }
  }

  return (
    <IonPage>
      <IonContent color="dark">
        <div className="login-container">
          <div className="login-input-container">
            <div className="callcentral-logo-container">
              <div className="logo-wrapper">
                <img
                  className="callcentral-logo"
                  src="assets/icon/logoIcon.svg"
                  alt="Nserix"
                />

              </div>
            </div>

            <div className="login-input-wrapper">
              {isLoaderActive && (
                <div className="loader-container">
                  <Loader></Loader>
                </div>
              )}
              <div className="login-input-header">
                <div className="welcome-wrapper">
                  <span className="welcome">Welcome!</span>
                </div>
                <br></br>
                <span className="header-title">Reset your password</span>
              </div>

              {isLinkValid ? (
                <form name="form" onSubmit={onSubmit}>
                  <div className="item-input-wrapper">
                    <div
                      className={`input-icon clickable ${isShowPassword
                          ? "icon-show_password"
                          : "icon-hide_password"
                        }`}
                      onClick={() => setPasswordStatus(!isShowPassword)}
                    ></div>
                    <IonItem className="item-input">
                      <IonLabel className="item-label" position="floating">
                        New Password
                      </IonLabel>
                      <IonInput
                        onIonBlur={onPassword}
                        className="ion-input"
                        type={isShowPassword ? "text" : "password"}
                        name="reset-password"
                        value={resetPassword}
                        onIonChange={handleResetChange}
                      >
                        {" "}
                      </IonInput>
                    </IonItem>
                    {validation.password.notStrong && (
                      <div className="feedback">
                        Must include 1 digit, 1 alphabet, 1 special character
                        and at least 8 characters long
                      </div>
                    )}
                  </div>

                  <div className="item-input-wrapper">
                    <div
                      className={`input-icon clickable ${isShowConfirmPassword
                          ? "icon-show_password"
                          : "icon-hide_password"
                        }`}
                      onClick={() =>
                        setConfirmPasswordStatus(!isShowConfirmPassword)
                      }
                    ></div>
                    <IonItem className="item-input">
                      <IonLabel className="item-label" position="floating">
                        Confirm Password
                      </IonLabel>
                      <IonInput
                        className="ion-input"
                        type={isShowConfirmPassword ? "text" : "password"}
                        name="confirm-password"
                        value={confirmPassword}
                        onIonChange={handleConfirmChange}
                      >
                        {" "}
                      </IonInput>
                    </IonItem>
                  </div>

                  <div className="messageStatus">{forgotStatus}</div>

                  <div>
                    <IonButton
                      className="button primary-btn login-btn btn-curved"
                      onClick={(e) => {
                        onSubmit(e);
                      }}
                      disabled={!isFormValid && validation.password.notStrong}
                    >
                      Reset Password
                    </IonButton>
                  </div>
                </form>
              ) : (
                <div className="link-expired">
                  {ERROR.ResetPassword.LINK_EXPIRED}
                </div>
              )}
            </div>
          </div>
          <div className="loginbg-container">
            <div className="bgimage-text-wrapper">
              <span className="white-text">
                The one-stop solution<br></br>
              </span>
              <span className="white-text">
                for platform-independent<br></br>
              </span>
              <span className="green-text">
                doctor management<br></br>
              </span>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ResetPasswordPage;
